import { useEffect, useState } from 'react';
import { apiFetch, type UserDetails } from '@/src/api/apiService';
import { API_BASE } from '@/src/api/config';

/**
 * Home of a partner-only account (A10b-1, D-057).
 *
 * The dashboard home only renders Owner and Driver profiles, so a partner who
 * signs in to the app landed on an empty screen. A partner-only account is one
 * with an explicit groups list holding neither Owner nor Driver and no owned
 * company, whose partner workspace the server confirms (200). Anything else
 * keeps its current route: Owner/Driver accounts (even when also partners),
 * staff without a partner record (403) and any failed check.
 */
export const PARTNER_HOME_ROUTE = '/partner';

type PartnerHomeUser = Pick<UserDetails, 'groups' | 'owned_companies'> | null | undefined;

export function isPartnerOnlyCandidate(userData: PartnerHomeUser): boolean {
  const groups = userData?.groups;
  if (!Array.isArray(groups)) return false;
  if (groups.includes('Owner') || groups.includes('Driver')) return false;
  return (userData?.owned_companies?.length ?? 0) === 0;
}

/** `/partner` for a server-confirmed partner-only account, otherwise null. */
export async function partnerHomeRoute(userData: PartnerHomeUser): Promise<typeof PARTNER_HOME_ROUTE | null> {
  if (!isPartnerOnlyCandidate(userData)) return null;
  try {
    const response = await apiFetch(`${API_BASE}/api/referrals/workspace/`, { includeCompanyContext: false }, true, false);
    return response.ok ? PARTNER_HOME_ROUTE : null;
  } catch {
    return null;
  }
}

/** 'checking' while a partner-only candidate is confirmed, then the route or null. */
export function usePartnerHomeRoute(userData: PartnerHomeUser): typeof PARTNER_HOME_ROUTE | null | 'checking' {
  const candidate = isPartnerOnlyCandidate(userData);
  const [route, setRoute] = useState<typeof PARTNER_HOME_ROUTE | null | 'checking'>(candidate ? 'checking' : null);
  useEffect(() => {
    if (!candidate) { setRoute(null); return; }
    let active = true;
    setRoute('checking');
    void partnerHomeRoute(userData).then(result => { if (active) setRoute(result); });
    return () => { active = false; };
    // Re-check only when the account's role facts change.
  }, [candidate]);
  return route;
}
